import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../utils/api';
import { usePolling } from '../hooks/useApi';
import { BALANCE_TOKENS, formatTokenAmount } from '../utils/balanceTokens';
import { formatHistoryTime } from '../utils/time';

const PLATFORM_LABELS: Record<string, string> = {
  ton: 'TON wallet',
  gate: 'Gate',
  mexc: 'MEXC',
  binance: 'Binance',
  bingx: 'BingX Spot',
  bitmart: 'BitMart',
  bybit: 'Bybit',
  weex: 'WEEX',
};

const MODE_LABELS: Record<string, string> = {
  off: 'Выкл',
  minMax: 'Min/Max',
  routeAware: 'По маршрутам',
};

const STATUS_LABELS: Record<string, string> = {
  disabled: 'выкл',
  balanced: 'balanced',
  needed: 'needed',
  in_transit: 'в пути',
  blocked: 'blocked',
};

const PLATFORMS = ['ton', 'gate', 'mexc', 'binance', 'bingx', 'bitmart', 'bybit', 'weex'] as const;
const BALANCE_TOKEN_ORDER = BALANCE_TOKENS.map(token => token.id);

function tokenOrder(token: string): number {
  const index = BALANCE_TOKEN_ORDER.indexOf(token.toUpperCase());
  return index === -1 ? BALANCE_TOKEN_ORDER.length : index;
}

function statusBadgeClass(status: string): string {
  if (status === 'balanced') return 'badge-success';
  if (status === 'needed') return 'badge-warning';
  if (status === 'blocked') return 'badge-danger';
  if (status === 'in_transit') return 'badge-warning';
  return 'badge-neutral';
}

function fieldKey(platform: string, kind: 'Min' | 'Max'): string {
  const camelPlatform = platform.replace(/_([a-z])/g, (_match, letter) => String(letter).toUpperCase());
  return camelPlatform + kind;
}

function planLabel(plan: any): string {
  if (!plan) return '-';
  const network = plan.network ? ' · ' + plan.network : '';
  const bridge = plan.bridgeFinalPlatform
    ? ' · дальше ' + plan.bridgeFinalPlatform.toUpperCase()
    : plan.bridgeRole === 'bridge_out'
      ? ' · bridge-out'
      : '';
  return plan.fromPlatform.toUpperCase() + ' -> ' + plan.toPlatform.toUpperCase()
    + network + bridge + ' · ' + formatTokenAmount(plan.token, plan.amount) + ' ' + plan.token;
}

export function AutoBalancePage() {
  const { data: configs, refetch: refetchConfigs } = usePolling(() => api.getAutoBalanceConfigs(), 15000);
  const { data: statuses, refetch: refetchStatuses } = usePolling(() => api.getAutoBalanceStatus(), 5000);
  const { data: transfers, refetch: refetchTransfers } = usePolling(() => api.getAutoBalanceTransfers(), 15000);
  const [drafts, setDrafts] = useState<Record<string, any>>({});
  const [saveState, setSaveState] = useState<Record<string, 'saving' | 'saved' | 'error'>>({});
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState('');
  const [addressToken, setAddressToken] = useState('USDT');
  const [addressNetwork, setAddressNetwork] = useState('TON');
  const [addressResult, setAddressResult] = useState<any>(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressMessage, setAddressMessage] = useState('');
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const saveSeqRef = useRef<Record<string, number>>({});
  const draftsRef = useRef<Record<string, any>>({});

  useEffect(() => {
    if (!configs) return;
    setDrafts(prev => {
      const next = { ...prev };
      for (const cfg of configs) {
        if (!next[cfg.token]) next[cfg.token] = { ...cfg };
      }
      return next;
    });
  }, [configs]);

  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  useEffect(() => {
    return () => {
      for (const timer of Object.values(saveTimersRef.current)) clearTimeout(timer);
    };
  }, []);

  const statusByToken = useMemo(() => {
    const map: Record<string, any> = {};
    for (const status of statuses || []) map[status.token] = status;
    return map;
  }, [statuses]);

  const rows = useMemo(() => {
    const orderedTokens = new Set<string>();
    for (const token of BALANCE_TOKEN_ORDER) orderedTokens.add(token);
    for (const status of statuses || []) orderedTokens.add(status.token);
    for (const cfg of configs || []) orderedTokens.add(cfg.token);
    return Array.from(orderedTokens).sort((a, b) => tokenOrder(a) - tokenOrder(b) || a.localeCompare(b)).map(token => ({
      token,
      cfg: drafts[token] || configs?.find((item: any) => item.token === token),
      status: statusByToken[token],
    }));
  }, [configs, drafts, statusByToken, statuses]);

  const executionEnabled = !!statuses?.some((status: any) => status.executionEnabled);

  function scheduleConfigSave(token: string, nextDraft: any) {
    if (saveTimersRef.current[token]) clearTimeout(saveTimersRef.current[token]);
    const seq = (saveSeqRef.current[token] || 0) + 1;
    saveSeqRef.current[token] = seq;
    setSaveState(prev => ({ ...prev, [token]: 'saving' }));

    saveTimersRef.current[token] = setTimeout(async () => {
      try {
        await api.updateAutoBalanceConfig(token, nextDraft);
        if (saveSeqRef.current[token] !== seq) return;
        setSaveState(prev => ({ ...prev, [token]: 'saved' }));
        refetchConfigs();
        refetchStatuses();
      } catch {
        if (saveSeqRef.current[token] !== seq) return;
        setSaveState(prev => ({ ...prev, [token]: 'error' }));
      }
    }, 450);
  }

  function updateDraft(token: string, patch: Record<string, unknown>) {
    const nextDraft = { ...(draftsRef.current[token] || configs?.find((item: any) => item.token === token) || {}), ...patch };
    draftsRef.current = { ...draftsRef.current, [token]: nextDraft };
    setDrafts(draftsRef.current);
    scheduleConfigSave(token, nextDraft);
  }

  function saveLabel(token: string): string {
    if (saveState[token] === 'saving') return 'сохраняю...';
    if (saveState[token] === 'saved') return 'сохранено';
    if (saveState[token] === 'error') return 'ошибка сохранения';
    return 'автосохранение';
  }

  async function runCheck() {
    setChecking(true);
    setMessage('');
    try {
      const result = await api.checkAutoBalance();
      if (result.executionResult?.executed) {
        setMessage('Запущен перевод: ' + planLabel(result.nextPlan) + ' · withdrawal ' + (result.executionResult.withdrawalId || '-'));
      } else if (result.executionResult?.attempted) {
        setMessage('Исполнение не прошло: ' + result.executionResult.reason);
      } else {
        setMessage(result.nextPlan ? 'Найден план: ' + planLabel(result.nextPlan) : 'Проверено: готовых переводов нет');
      }
      refetchStatuses();
      refetchTransfers();
    } catch (e: any) {
      setMessage('Ошибка проверки: ' + (e.message || 'unknown'));
    } finally {
      setChecking(false);
    }
  }

  async function loadDepositAddresses() {
    setAddressLoading(true);
    setAddressMessage('');
    try {
      const result = await api.getAutoBalanceDepositAddresses(addressToken, addressNetwork);
      setAddressResult(result);
    } catch (e: any) {
      setAddressMessage('Ошибка адресов: ' + (e.message || 'unknown'));
    } finally {
      setAddressLoading(false);
    }
  }

  useEffect(() => {
    loadDepositAddresses();
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="section-title" style={{ margin: 0 }}>Автобаланс Min/Max</div>
          <div className="text-xs text-secondary">
            {executionEnabled
              ? 'Боевой режим: кнопка проверки может запустить поддержанный withdrawal.'
              : 'Dry-run: считаем план, реальные отправки выключены.'}
          </div>
        </div>
        <button className="btn btn-sm btn-primary" onClick={runCheck} disabled={checking}>
          {checking ? 'Проверяем...' : executionEnabled ? 'Проверить / исполнить' : 'Проверить сейчас'}
        </button>
      </div>

      {message && <div className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{message}</div>}

      <div className="auto-balance-list">
        {rows.map(({ token, cfg, status }) => {
          if (!cfg) return null;
          return (
            <div key={token} className="auto-balance-row">
              <div className="auto-balance-head">
                <div className="flex items-center gap-2">
                  <span className="route-token">{token}</span>
                  <span className={'badge ' + statusBadgeClass(status?.status || 'disabled')}>
                    {STATUS_LABELS[status?.status || 'disabled'] || status?.status}
                  </span>
                  <span className="text-xs text-secondary">{status?.reason || 'нет статуса'}</span>
                </div>
                <span className="text-xs text-secondary">{saveLabel(token)}</span>
              </div>

              <div className="auto-balance-controls">
                <label className="route-field auto-balance-switch">
                  <span>Вкл</span>
                  <input
                    type="checkbox"
                    checked={!!cfg.enabled}
                    onChange={e => updateDraft(token, { enabled: e.target.checked })}
                  />
                </label>
                <div className="route-field">
                  <label>Режим</label>
                  <select value={cfg.mode || 'off'} onChange={e => updateDraft(token, { mode: e.target.value })}>
                    <option value="off">{MODE_LABELS.off}</option>
                    <option value="minMax">{MODE_LABELS.minMax}</option>
                    <option value="routeAware">{MODE_LABELS.routeAware} (позже)</option>
                  </select>
                </div>
                <div className="route-field">
                  <label>Итого</label>
                  <span className="text-mono text-xs">{status ? formatTokenAmount(token, status.total) : '-'}</span>
                </div>
                <div className="route-field">
                  <label>Среднее / {PLATFORMS.length}</label>
                  <span className="text-mono text-xs">{status ? formatTokenAmount(token, status.average) : '-'}</span>
                </div>
                <div className="route-field">
                  <label>План</label>
                  <span className="text-mono text-xs">{planLabel(status?.plan)}</span>
                </div>
              </div>

              <div className="auto-balance-platforms">
                {PLATFORMS.map(platform => {
                  const platformStatus = status?.platforms?.find((item: any) => item.platform === platform);
                  const minKey = fieldKey(platform, 'Min');
                  const maxKey = fieldKey(platform, 'Max');
                  return (
                    <div key={platform} className="auto-balance-platform">
                      <div className="auto-balance-platform-title">
                        <span>{PLATFORM_LABELS[platform]}</span>
                        <span className="text-mono">{platformStatus ? formatTokenAmount(token, platformStatus.amount) : '-'}</span>
                      </div>
                      <div className="auto-balance-minmax">
                        <div className="route-field">
                          <label>Min</label>
                          <input value={cfg[minKey] || ''} onChange={e => updateDraft(token, { [minKey]: e.target.value })} />
                        </div>
                        <div className="route-field">
                          <label>Max</label>
                          <input value={cfg[maxKey] || ''} onChange={e => updateDraft(token, { [maxKey]: e.target.value })} />
                        </div>
                      </div>
                      {platformStatus && (
                        <div className="text-xs text-secondary">
                          {platformStatus.active === false
                            ? 'отключено: Min/Max 0/0'
                            : 'surplus ' + formatTokenAmount(token, platformStatus.surplus) + ' · capacity ' + formatTokenAmount(token, platformStatus.capacity)
                              + (platformStatus.ageSec !== null ? ' · ' + platformStatus.ageSec + 'с' : '')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {status?.blockedReasons?.length > 0 && (
                <div className="auto-balance-reasons">
                  {status.blockedReasons.slice(0, 3).map((reason: string) => <span key={reason}>{reason}</span>)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="section-title mt-2">История автобаланса</div>
      <div className="auto-balance-row mb-2">
        <div className="auto-balance-head">
          <div>
            <div className="section-title" style={{ margin: 0 }}>Адреса для сверки</div>
            <div className="text-xs text-secondary">Read-only: TON wallet из конфига, CEX deposit address из API бирж.</div>
          </div>
          <button className="btn btn-sm btn-outline" onClick={loadDepositAddresses} disabled={addressLoading}>
            {addressLoading ? 'Спрашиваю...' : 'Обновить адреса'}
          </button>
        </div>
        <div className="address-controls">
          <div className="route-field">
            <label>Токен</label>
            <input value={addressToken} onChange={e => setAddressToken(e.target.value.toUpperCase())} />
          </div>
          <div className="route-field">
            <label>Сеть</label>
            <input value={addressNetwork} onChange={e => setAddressNetwork(e.target.value.toUpperCase())} placeholder="TON, пусто = все" />
          </div>
          <div className="route-field">
            <label>CEX coin</label>
            <span className="text-mono text-xs">{addressResult?.coin || '-'}</span>
          </div>
        </div>
        {addressMessage && <div className="text-xs text-secondary mb-2">{addressMessage}</div>}
        <div className="address-table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Площадка</th>
                <th>Coin</th>
                <th>Network</th>
                <th>Address</th>
                <th>Memo / Tag</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {addressResult?.addresses?.map((row: any, idx: number) => (
                <tr key={row.platform + row.network + idx}>
                  <td>{row.platformLabel}</td>
                  <td className="text-mono">{row.coin}</td>
                  <td className="text-mono">{row.network || '-'}</td>
                  <td className="address-cell">
                    {row.address || '-'}
                    {row.rawAddress && <div className="text-xs text-muted">raw: {row.rawAddress}</div>}
                  </td>
                  <td className="address-cell">{row.memo ? ((row.memoName || 'memo') + ': ' + row.memo) : '-'}</td>
                  <td>
                    <span className={'badge ' + (row.status === 'ok' ? 'badge-success' : row.status === 'error' ? 'badge-danger' : 'badge-warning')}>
                      {row.status}
                    </span>
                    {row.error && <div className="text-xs text-secondary">{row.error}</div>}
                  </td>
                </tr>
              ))}
              {(!addressResult?.addresses || addressResult.addresses.length === 0) && (
                <tr><td colSpan={6} className="text-secondary text-xs">Адреса ещё не загружены</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Токен</th>
            <th>Откуда</th>
            <th>Куда</th>
            <th>Сеть</th>
            <th>Сумма</th>
            <th>Статус</th>
            <th>Причина</th>
            <th>Когда</th>
          </tr>
        </thead>
        <tbody>
          {transfers?.slice(0, 12).map((transfer: any) => (
            <tr key={transfer.id}>
              <td className="route-token">{transfer.token}</td>
              <td>{transfer.fromPlatform}</td>
              <td>{transfer.toPlatform}</td>
              <td className="text-xs text-mono">{transfer.network || '-'}</td>
              <td className="text-mono">{formatTokenAmount(transfer.token, transfer.amount)}</td>
              <td><span className={'badge ' + statusBadgeClass(transfer.status)}>{transfer.status}</span></td>
              <td className="text-xs text-secondary" title={transfer.reason || ''}>{transfer.reason || '-'}</td>
              <td className="text-xs text-secondary" title={transfer.createdAt}>{formatHistoryTime(transfer.createdAt)}</td>
            </tr>
          ))}
          {(!transfers || transfers.length === 0) && (
            <tr><td colSpan={8} className="text-secondary text-xs">Переводов пока нет</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
